import { armTagsApiRef } from '@internal/plugin-azure-resources-common-react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';

import {
  buildResourceGroupRows,
  type FailingResourceGroup,
  type ResourceGroupRow,
} from './buildResourceGroupRows';

/**
 * Resolves the scorecard's resource groups against Azure. Those reported with a
 * subscription are looked up in that subscription only.
 *
 * Bare names from older payloads go in two passes: the team's own subscriptions
 * first, then tenant-wide for whatever is left. The first pass keeps names that
 * repeat across subscriptions from fanning out, the second keeps aggregated
 * (parent organisation) scorecards working, where the resource groups sit in a
 * child team's subscriptions.
 */
export const useResourceGroupResolution = (
  resourceGroups: FailingResourceGroup[],
  tagKey: string,
  teamSubscriptionIds: string[],
  enabled = true,
): { loading: boolean; error?: Error; rows?: ResourceGroupRow[] } => {
  const armTagsApi = useApi(armTagsApiRef);

  const { loading, error, value } = useAsync(async () => {
    if (!enabled) return undefined;
    if (!resourceGroups.length) return [];

    const located = resourceGroups.filter(group => group.subscriptionId);
    const names = resourceGroups
      .filter(group => !group.subscriptionId)
      .map(group => group.name);

    const inReportedSubscriptions = located.length
      ? await armTagsApi.resolveResourceGroups(
          located.map(group => group.name),
          Array.from(
            new Set(located.map(group => group.subscriptionId as string)),
          ),
        )
      : [];

    const inTeamSubscriptions =
      names.length && teamSubscriptionIds.length
        ? await armTagsApi.resolveResourceGroups(names, teamSubscriptionIds)
        : [];

    const found = new Set(
      inTeamSubscriptions.map(resourceGroup =>
        resourceGroup.name.toLowerCase(),
      ),
    );
    const stillMissing = names.filter(name => !found.has(name.toLowerCase()));

    const elsewhere = stillMissing.length
      ? await armTagsApi.resolveResourceGroups(stillMissing)
      : [];

    return buildResourceGroupRows({
      resourceGroups,
      resolved: [
        ...inReportedSubscriptions,
        ...inTeamSubscriptions,
        ...elsewhere,
      ],
      teamSubscriptionIds,
      tagKey,
    });
  }, [armTagsApi, resourceGroups, tagKey, teamSubscriptionIds, enabled]);

  return { loading, error, rows: value };
};
